import { Modifier } from '@dnd-kit/abstract';
import { Feedback } from '@dnd-kit/dom';
import { getFixedPositionOffset, isSafari } from '@dnd-kit/dom/utilities';
import { useSortable } from '@dnd-kit/react/sortable';
import { useTranslation } from 'react-i18next';
import { href, useLocation, useNavigate } from 'react-router';

import { DealCard } from '@crm/entities/deal';
import { cn, tw } from '@crm/shared/lib/styles/utils';
import { ROUTES } from '@crm/shared/routes';

import { DealCardDeleteBtn } from '../DealCardDeleteBtn';

import type { IDealCardData } from '@crm/entities/deal';
import type { BackNavigationState } from '@crm/shared/routes';
import type { DragOperation } from '@dnd-kit/abstract';

class CompensateSafariFixedOffset extends Modifier {
    // dnd-kit requires a class-based modifier even though this transform is stateless.
    // eslint-disable-next-line class-methods-use-this
    apply({ transform }: DragOperation) {
        const fixedOffset = getFixedPositionOffset();

        return {
            x: transform.x - fixedOffset.x,
            y: transform.y - fixedOffset.y,
        };
    }
}

const dealCardSortableModifiers =
    typeof navigator !== 'undefined' && isSafari() ? [CompensateSafariFixedOffset] : undefined;

const dealCardSortablePlugins = [
    Feedback.configure({
        feedback: 'clone',
    }),
];

export const DraggableDealCard = ({
    id,
    index,
    group,
    dealCardData,
    isHiddenPlaceholder = false,
    isPlaceholder = false,
}: {
    id: string;
    index: number;
    group: string;
    dealCardData: IDealCardData;
    isHiddenPlaceholder?: boolean;
    isPlaceholder?: boolean;
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { ref, isDragging } = useSortable({
        id: `deal-${id}`,
        index,
        type: 'deal',
        accept: 'deal',
        group,
        data: { dealId: id },
        modifiers: dealCardSortableModifiers,
        plugins: dealCardSortablePlugins,
    });

    const dealCardPlaceholderStyles = tw(
        'data-[dnd-dragging=true]:bg-black/2 data-[dnd-dragging=true]:shadow-none data-[dnd-dragging=true]:*:invisible data-[dnd-placeholder=clone]:bg-black/2 data-[dnd-placeholder=clone]:shadow-none data-[dnd-placeholder=clone]:*:invisible'
    );
    const dealDetailsPath = href(ROUTES.DEAL, { id: String(id) });

    const handleClick = () => {
        navigate(dealDetailsPath, {
            state: { fromRoute: location.pathname } satisfies BackNavigationState,
        });
    };

    return (
        <div
            tabIndex={0}
            ref={ref}
            aria-label={t('crm.deals.card.openAriaLabel', {
                dealName: dealCardData.name,
            })}
            data-deal-drag-placeholder={isPlaceholder ? true : undefined}
            className={cn(
                'w-full rounded-lg transition-shadow data-[dnd-dragging=true]:z-50 data-[dnd-dragging=true]:overflow-hidden',
                dealCardPlaceholderStyles,
                !isHiddenPlaceholder && 'data-[dnd-dragging=true]:relative',
                isHiddenPlaceholder && 'pointer-events-none absolute inset-x-0 opacity-0',
                isPlaceholder && 'bg-black/2 shadow-none *:invisible',
                isDragging && 'cursor-grabbing',
                !isDragging && 'cursor-grab'
            )}
            onClick={handleClick}
            role="link"
        >
            <DealCard
                dealCardData={dealCardData}
                deleteBtnSlot={
                    import.meta.env.DEV ? (
                        <DealCardDeleteBtn dealId={dealCardData.id} dealName={dealCardData.name} />
                    ) : undefined
                }
            />
        </div>
    );
};
